<template>
  <!-- Second row of the Tasks header. Bookings and Traffic have no equivalent
       because their only input is the range; these are the dimension filters
       that used to live in a separate bar below the header. -->
  <div class="flex flex-wrap items-center gap-2">
    <div v-if="store.filters.range === 'custom'" class="flex items-center gap-1.5">
      <DatePicker :model-value="store.filters.startDate" @update:model-value="v => store.setFilters({ startDate: v })" placeholder="From" input-class="px-2.5 py-1 text-xs rounded-lg w-32" />
      <span class="text-xs text-gray-400">to</span>
      <DatePicker :model-value="store.filters.endDate" @update:model-value="v => store.setFilters({ endDate: v })" placeholder="To" input-class="px-2.5 py-1 text-xs rounded-lg w-32" />
    </div>

    <FilterDropdown label="Priority" :options="PRIORITY_OPTIONS" :selected="store.filters.priorities" @update="v => store.setFilters({ priorities: v })" />
    <FilterDropdown label="Colour" :options="COLOR_OPTIONS" :selected="store.filters.colors" swatches @update="v => store.setFilters({ colors: v })" />

    <FilterSelect
      label="Type"
      :model-value="store.filters.taskType"
      :options="TASK_TYPE_OPTIONS"
      default-value="all"
      @update:model-value="v => store.setFilters({ taskType: v })"
    />

    <label class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
      <input
        type="checkbox"
        :checked="store.filters.includeCanceled"
        @change="store.setFilters({ includeCanceled: $event.target.checked })"
        class="rounded text-blue-600 focus:ring-blue-500"
      />
      Include cancelled
    </label>

    <button
      v-if="store.hasActiveFilters"
      type="button"
      @click="store.resetFilters()"
      class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
    >Reset</button>

    <div class="ml-auto">
      <ExportMenu
        basename="task-stats"
        :payload="store.stats"
        :csv="store.stats ? taskStatsCsv(store.stats, store.filters) : null"
        :stamp="store.stats ? `${store.stats.range.start}_to_${store.stats.range.end}` : ''"
      />
    </div>
  </div>
</template>

<script setup>
import { useTaskStatsStore } from 'stores/taskStats.js'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import FilterDropdown from '../shared/FilterDropdown.vue'
import FilterSelect from '../shared/FilterSelect.vue'
import ExportMenu from '../shared/ExportMenu.vue'
import { taskStatsCsv } from './taskStatsCsv.js'
import { TASK_COLORS, TASK_COLOR_HEX } from 'controllers/vue_components/tasks/taskColors.js'
import { PRIORITY_ORDER, PRIORITY_META } from 'controllers/vue_components/tasks/taskPriorities.js'

const store = useTaskStatsStore()

// The descriptions are the reason this is not a native select. "Recurring"
// especially does not mean what people assume: the stats count the individual
// occurrences a schedule produces, never the schedule row itself, which is not
// a task anyone works on. Saying so here is the only place that
// misunderstanding gets corrected at the moment it matters.
const TASK_TYPE_OPTIONS = [
  {
    value: 'all',
    label: 'All types',
    description: 'One time tasks and recurring occurrences counted together.',
  },
  {
    value: 'one_time',
    label: 'One time',
    description: 'Standalone tasks you created yourself, each with a single due date.',
  },
  {
    value: 'recurring',
    label: 'Recurring',
    description: 'The individual occurrences a repeating schedule produces. The schedule itself is not counted.',
  },
]

const PRIORITY_OPTIONS = PRIORITY_ORDER.map(v => ({ value: v, label: PRIORITY_META[v].label }))
const COLOR_OPTIONS = TASK_COLORS.map(c => ({ value: c.value, label: c.label, hex: TASK_COLOR_HEX[c.value] }))
</script>
